import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import type { VisualDxRankedDifferentialEntry } from '../../contracts';
import { VisualDxDiagnosisRow } from './VisualDxDiagnosisRow';

/**
 * Heidi-native ranked-differential card list — the visual companion
 * to the input flow's "Build differential" CTA. Mounted by
 * ``InteractiveWidgetBlock`` for the ``visualdx_ranked_differential``
 * widget kind whenever the LLM calls ``render_visualdx_ranked_differential``
 * and the BE acks with the merged payload.
 *
 * Each row is a ``VisualDxDiagnosisRow`` (shared with the drug-reaction
 * list): rank chip, thumbnail (graceful blank if MCP enrichment failed),
 * diagnosis name + one-line description, urgency badge, ordinal confidence
 * bar (relative ranking only — no numeric label, see SaMD note below), and
 * a chevron when an enrichment URL is present. The optional ``onSelect``
 * callback fires on click so the parent can dispatch a follow-up chat turn —
 * this component stays purely presentational.
 *
 * SaMD note: the bar width is derived from the LLM's
 * ``confidence_pct`` but the numeric value is intentionally NOT
 * surfaced (visually or via ATs). Showing a precise percentage risks
 * being read as a calibrated clinical probability; a bare bar reads
 * as ordinal relative ranking, which matches what the model actually
 * produces.
 *
 * Styling: all colors and typography come from semantic design-system
 * tokens (`bg-surface`, `text-text-primary`, `bg-validation-*`,
 * `<TypographyH5>`, etc.) — no hardcoded hex, no inline font sizes.
 *
 * i18n note: per PR #9851 review, this surface is intentionally
 * English-only for the EVI-1349 MVP; strings will be migrated to
 * react-intl + Crowdin in a follow-up ticket.
 */

interface VisualDxRankedDifferentialProps {
  differential: VisualDxRankedDifferentialEntry[];
  onSelect?: (entry: VisualDxRankedDifferentialEntry) => void;
}

interface UrgencyMeta {
  badgeClass: string;
  label: string;
}

const URGENCY_META: Record<VisualDxRankedDifferentialEntry['urgency'], UrgencyMeta> = {
  routine: {
    badgeClass: 'bg-validation-success-muted text-validation-success',
    label: 'Routine',
  },
  moderate: {
    badgeClass: 'bg-validation-warning-muted text-validation-warning',
    label: 'Moderate',
  },
  urgent: {
    badgeClass: 'bg-validation-destructive-muted text-validation-destructive',
    label: 'Urgent',
  },
};

const containerClass =
  'flex w-full flex-col gap-2.5 rounded-xl bg-background-secondary p-4 text-text-primary';

const headerClass = 'flex items-baseline justify-between pb-1';

export const VisualDxRankedDifferential = ({
  differential,
  onSelect,
}: VisualDxRankedDifferentialProps): JSX.Element => {
  if (!differential || differential.length === 0) {
    return (
      <div className={containerClass}>
        <TypographyCaption className="text-text-secondary">
          No ranked differential available.
        </TypographyCaption>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <header className={headerClass}>
        <TypographyP2Bold className="text-text-primary">
          Ranked differential
        </TypographyP2Bold>
        <TypographyCaption className="text-text-secondary">
          {differential.length} candidates &middot; tap to drill down
        </TypographyCaption>
      </header>

      {differential.map((entry) => {
        const meta = URGENCY_META[entry.urgency];
        return (
          <VisualDxDiagnosisRow
            key={`${entry.rank}-${entry.name}`}
            rank={entry.rank}
            name={entry.name}
            imageUrl={entry.image_url}
            description={entry.description}
            badge={{ label: meta.label, className: meta.badgeClass }}
            bar={{ pct: entry.confidence_pct, testId: 'visualdx-confidence-bar-fill' }}
            // SaMD-tone hardening (PR #9359 BE review): label the bar as an
            // AI estimate so users don't read it as a clinical probability.
            footnote="AI-estimated relative ranking — not a clinical probability."
            onClick={onSelect ? () => onSelect(entry) : undefined}
            showChevron={Boolean(entry.visualdx_url)}
            // Positional ranking only — the numeric confidence_pct is
            // deliberately omitted here (and visually).
            ariaLabel={`${entry.name}, rank ${entry.rank} of ${differential.length}, ${meta.label} urgency`}
          />
        );
      })}
    </div>
  );
};
